import { DashTreeSourceNode, loadDashboardTree } from 'src/component/dashboardTree/dashboardTreeSource';
import { GLOB } from 'src/util/Glob';

export interface AutoplayQueueItem {
  id: string;
  label: string;
}

/**
 * Which id space a playable container belongs to. Folder ids come from `dashboard_folder_id` and
 * user group ids from `group_id`, so the same number can address both - the kind has to be carried
 * from the nav tree or the wrong container gets played.
 */
export type AutoplayFolderKind = 'folder' | 'user_group';

/**
 * Direct children of a dashboard folder, or the top level when `folderId` is null (the tree root).
 * Returns undefined when the folder is gone - the nav tree can be older than the server.
 */
function findChildren(nodes: DashTreeSourceNode[], folderId: string, kind: AutoplayFolderKind): DashTreeSourceNode[] | undefined {
  for (const node of nodes) {
    if (node.type === kind && node.id === folderId) return node.children ?? [];

    const found = node.children && findChildren(node.children, folderId, kind);
    if (found) return found;
  }
}

/**
 * Dashboards to play for a folder, in natural order by label. Built from the tree endpoint because
 * the left nav loads folder contents lazily. Nested folders are not descended into.
 *
 * This is the nav tree's own leaf order with one exception: `MenuService.getFolderSorter` floats
 * the folder's default dashboard to the top, and the `v1/tree` DTO carries no default flag to
 * reproduce that with. The start modal shows the resulting order, so it is never a surprise.
 */
export async function loadFolderQueue(folderId: string | null, kind: AutoplayFolderKind, signal: AbortSignal): Promise<AutoplayQueueItem[] | undefined> {
  const tree = await loadDashboardTree(signal);
  const children = folderId == null ? tree : findChildren(tree, folderId, kind);
  if (!children) return undefined;

  return children
    .filter(node => node.type === 'dashboard')
    .map(node => ({ id: node.id, label: node.label }))
    .sort((a, b) => GLOB.naturalSort(a.label, b.label));
}
